import type { DebouncedFunc } from "lodash-es/debounce";

import * as React from "react";

import debounce from "lodash-es/debounce";

import { isNullish } from "../lib/misc";
import { unsafe_useLayoutEffectWithoutWarning as useLayoutEffect } from "./use-layout-effect";

export function useDebouncedCallback<
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  CallbackType extends (...args: any[]) => any,
>(callback: CallbackType, wait: number, options?: DebounceOptions) {
  const savedCallback = React.useRef(callback);
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const { leading = false, trailing = true, maxWait = null } = options ?? {};

  const debouncedCallback = React.useMemo<DebouncedFunc<CallbackType>>(() => {
    const options: DebounceOptions = {
      leading,
      trailing,
      maxWait: maxWait ?? undefined,
    };
    if (isNullish(wait) || wait <= 0) {
      // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
      const handler = (...args: any[]) => {
        savedCallback.current?.(...args);
      };
      handler.cancel = () => void 0;
      handler.flush = () => void 0;
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      return handler as DebouncedFunc<CallbackType>;
    }

    const debounced = debounce(
      // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
      (...args: any[]) => {
        return savedCallback.current?.(...args);
      },
      wait,
      options,
    );
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return debounced as DebouncedFunc<CallbackType>;
  }, [leading, maxWait, trailing, wait]);

  const previousCallbackRef = React.useRef(debouncedCallback);
  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    const previousCallback = previousCallbackRef.current;
    previousCallbackRef.current = debouncedCallback;
    const flush = debouncedCallback.flush;
    if (previousCallback !== debouncedCallback) {
      return () => {
        // NOTE (Chance 2024-03-29): If the callback reference changes we want
        // to flush pending calls before the next is called, but we only want to
        // do this once. Hence the comparison.
        flush();
      };
    }
  }, [debouncedCallback]);

  return debouncedCallback;
}

interface DebounceOptions {
  /**
   * Specify invoking on the leading edge of the timeout. Defaults to `false`.
   */
  leading?: boolean;
  /**
   * The maximum time the callback is allowed to be delayed before it's invoked.
   * Defaults to `0`.
   */
  maxWait?: number;
  /**
   * Specify invoking on the trailing edge of the timeout. Defaults to `true`.
   */
  trailing?: boolean;
}
